import axios from 'axios'

const VAPID_PUBLIC_KEY = 'BCZMvDvmPMAsELYQe8zoHosa5AMWOGcmkPUVwJmooxoZD6c26Oj8xYeL-VK-QdvQJodzxsq6efSLU5Tx1WlciPY'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3004/api'

interface PushNotificationService {
  isSupported(): boolean
  requestPermission(): Promise<NotificationPermission>
  subscribe(): Promise<PushSubscription | null>
  unsubscribe(): Promise<boolean>
  sendNotification(title: string, options?: NotificationOptions): Promise<void>
  getSubscription(): Promise<PushSubscription | null>
}

class WebPushNotificationService implements PushNotificationService {
  private registration: ServiceWorkerRegistration | null = null

  constructor() {
    this.initializeServiceWorker()
  }

  private async initializeServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        this.registration = await navigator.serviceWorker.ready
      } catch (error) {
        console.error('Service worker registration failed:', error)
      }
    }
  }

  isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    )
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported')
    }

    let permission = Notification.permission

    if (permission === 'default') {
      permission = await Notification.requestPermission()
    }

    return permission
  }

  async subscribe(): Promise<PushSubscription | null> {
    if (!this.registration) {
      await this.initializeServiceWorker()
    }

    if (!this.registration) {
      throw new Error('Service worker not available')
    }

    const permission = await this.requestPermission()
    if (permission !== 'granted') {
      throw new Error('Notification permission denied')
    }

    try {
      const applicationServerKey = this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY)

      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      })

      // Save subscription to backend for server-side push
      await this.saveSubscriptionToBackend(subscription)

      return subscription
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error)
      return null
    }
  }

  private async saveSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      await axios.post(`${API_BASE}/push/subscribe`, {
        subscription: subscription.toJSON()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      console.log('✅ Push subscription saved to backend')
    } catch (error) {
      console.warn('Failed to save push subscription to backend:', error)
    }
  }

  async unsubscribe(): Promise<boolean> {
    if (!this.registration) {
      return false
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription()
      if (subscription) {
        // Remove from backend
        try {
          const token = localStorage.getItem('token')
          if (token) {
            await axios.post(`${API_BASE}/push/unsubscribe`, {
              endpoint: subscription.endpoint
            }, {
              headers: { Authorization: `Bearer ${token}` }
            })
          }
        } catch (_) { /* ignore */ }

        const successful = await subscription.unsubscribe()
        return successful
      }
      return true
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error)
      return false
    }
  }

  async sendNotification(title: string, options: NotificationOptions = {}): Promise<void> {
    const permission = await this.requestPermission()
    if (permission !== 'granted') {
      throw new Error('Notification permission denied')
    }

    const defaultOptions: any = {
      icon: '/android-chrome-192x192.png',
      badge: '/android-chrome-192x192.png',
      vibrate: [100, 50, 100],
      data: {
        dateOfArrival: Date.now(),
        primaryKey: 1
      },
      actions: [
        {
          action: 'explore',
          title: 'View Details',
          icon: '/android-chrome-192x192.png'
        },
        {
          action: 'close',
          title: 'Close',
          icon: '/android-chrome-192x192.png'
        }
      ]
    }

    const notificationOptions = { ...defaultOptions, ...options }

    if (this.registration) {
      this.registration.showNotification(title, notificationOptions)
    } else {
      new Notification(title, notificationOptions)
    }
  }

  async getSubscription(): Promise<PushSubscription | null> {
    if (!this.registration) {
      await this.initializeServiceWorker()
    }

    if (!this.registration) {
      return null
    }

    try {
      return await this.registration.pushManager.getSubscription()
    } catch (error) {
      console.error('Failed to get push subscription:', error)
      return null
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
  }
}

// Notification types for restaurant analytics
export interface RestaurantNotification {
  type: 'subscription_expiry' | 'payment_due' | 'system_alert' | 'new_order' | 'low_inventory' | 'daily_reminder'
  title: string
  message: string
  data?: Record<string, any>
  urgency?: 'low' | 'normal' | 'high'
}

export class RestaurantNotificationManager {
  private pushService: WebPushNotificationService
  private notificationQueue: RestaurantNotification[] = []
  private isProcessing = false

  constructor() {
    this.pushService = new WebPushNotificationService()
  }

  async initialize(): Promise<boolean> {
    if (!this.pushService.isSupported()) {
      console.warn('Push notifications are not supported in this browser')
      return false
    }

    try {
      const permission = await this.pushService.requestPermission()
      if (permission === 'granted') {
        await this.pushService.subscribe()
        this.setupNotificationHandlers()
        return true
      } else {
        console.warn('Notification permission denied')
        return false
      }
    } catch (error) {
      console.error('Failed to initialize push notifications:', error)
      return false
    }
  }

  async sendNotification(notification: RestaurantNotification): Promise<void> {
    const options: any = {
      body: notification.message,
      icon: '/android-chrome-192x192.png',
      badge: '/android-chrome-192x192.png',
      tag: notification.type,
      data: {
        type: notification.type,
        ...notification.data
      },
      requireInteraction: notification.urgency === 'high',
      silent: notification.urgency === 'low'
    }

    // Add type-specific styling and actions
    switch (notification.type) {
      case 'subscription_expiry':
        options.actions = [
          { action: 'renew', title: 'Renew Subscription', icon: '/android-chrome-192x192.png' },
          { action: 'dismiss', title: 'Dismiss', icon: '/android-chrome-192x192.png' }
        ]
        break
      case 'payment_due':
        options.actions = [
          { action: 'pay', title: 'Pay Now', icon: '/android-chrome-192x192.png' },
          { action: 'remind', title: 'Remind Later', icon: '/android-chrome-192x192.png' }
        ]
        break
      case 'new_order':
        options.actions = [
          { action: 'view', title: 'View Order', icon: '/android-chrome-192x192.png' },
          { action: 'accept', title: 'Accept', icon: '/android-chrome-192x192.png' }
        ]
        break
      case 'daily_reminder':
        options.actions = [
          { action: 'open_dashboard', title: 'Check Dashboard', icon: '/android-chrome-192x192.png' },
          { action: 'dismiss', title: 'Later', icon: '/android-chrome-192x192.png' }
        ]
        break
      case 'system_alert':
      case 'low_inventory':
        options.actions = [
          { action: 'view', title: 'View Details', icon: '/android-chrome-192x192.png' },
          { action: 'dismiss', title: 'Dismiss', icon: '/android-chrome-192x192.png' }
        ]
        break
    }

    await this.pushService.sendNotification(notification.title, options)
  }

  async scheduleNotification(notification: RestaurantNotification, delay: number = 0): Promise<void> {
    if (delay > 0) {
      setTimeout(() => {
        this.notificationQueue.push(notification)
        this.processQueue()
      }, delay)
    } else {
      this.notificationQueue.push(notification)
      this.processQueue()
    }
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.notificationQueue.length === 0) {
      return
    }

    this.isProcessing = true

    while (this.notificationQueue.length > 0) {
      const notification = this.notificationQueue.shift()!
      try {
        await this.sendNotification(notification)
        // Small delay between notifications to avoid spam
        await new Promise(resolve => setTimeout(resolve, 500))
      } catch (error) {
        console.error('Failed to send notification:', error)
      }
    }

    this.isProcessing = false
  }

  private setupNotificationHandlers(): void {
    // Handle notification clicks
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'notification-click') {
          this.handleNotificationAction(event.data.action, event.data.notificationData)
        }
      })
    }
  }

  private handleNotificationAction(action: string, data: any): void {
    switch (action) {
      case 'renew':
        // Navigate to subscription renewal page
        window.location.href = '/admin/subscriptions'
        break
      case 'pay':
        // Navigate to payment page
        window.location.href = '/payment'
        break
      case 'open_dashboard':
        // Navigate to dashboard
        window.location.href = '/dashboard'
        break
      case 'view':
        // Navigate to relevant view
        if (data.type === 'new_order') {
          window.location.href = '/restaurant/orders'
        } else {
          window.location.href = '/dashboard'
        }
        break
      case 'accept':
        // Handle order acceptance
        console.log('Order accepted:', data)
        break
      default:
        // Default action - open the app
        window.focus()
        break
    }
  }

  async getSubscriptionStatus(): Promise<{ subscribed: boolean; subscription?: PushSubscription }> {
    const subscription = await this.pushService.getSubscription()
    return {
      subscribed: !!subscription,
      subscription: subscription || undefined
    }
  }

  async toggleSubscription(): Promise<boolean> {
    const { subscribed } = await this.getSubscriptionStatus()
    
    if (subscribed) {
      return await this.pushService.unsubscribe()
    } else {
      const subscription = await this.pushService.subscribe()
      return !!subscription
    }
  }
}

export const notificationManager = new RestaurantNotificationManager()
export default WebPushNotificationService